// @mui
import { Box, Link, ListItemText, Tooltip } from '@mui/material';
import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { NavItemProps } from '../types';

// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
// locales
import { useLocales } from '../../../locales';
//
import Iconify from '../../iconify';
import { StyledIcon, StyledItem } from './styles';

// ----------------------------------------------------------------------

const NavItem = forwardRef<HTMLDivElement, NavItemProps>(
  ({ active, depth, isExternalLink, item, open, ...other }, ref) => {
    const { translate } = useLocales();

    const { caption, children, disabled, icon, info, path, roles, title } =
      item;

    const subItem = depth !== 1;

    const renderContent = (
      <StyledItem
        active={active}
        depth={depth}
        disabled={disabled}
        open={open}
        ref={ref}
        {...other}
      >
        {icon && <StyledIcon>{icon}</StyledIcon>}

        <ListItemText
          primary={`${translate(title)}`}
          primaryTypographyProps={{
            component: 'span',
            noWrap: true,
            variant: active ? 'subtitle2' : 'body2',
          }}
        />

        {info && (
          <Box component="span" sx={{ lineHeight: 0, ml: 1 }}>
            {info}
          </Box>
        )}

        {caption && (
          <Tooltip arrow title={`${translate(caption)}`}>
            <Box component="span" sx={{ lineHeight: 0, ml: 0.5 }}>
              <Iconify icon="eva:info-outline" width={16} />
            </Box>
          </Tooltip>
        )}

        {!!children && (
          <Iconify
            icon={subItem ? 'eva:chevron-right-fill' : 'eva:chevron-down-fill'}
            sx={{ flexShrink: 0, ml: 0.5 }}
            width={16}
          />
        )}
      </StyledItem>
    );

    const renderItem = () => {
      // ExternalLink
      if (isExternalLink)
        return (
          <Link href={path} rel="noopener" target="_blank" underline="none">
            {renderContent}
          </Link>
        );

      // Default
      return (
        <Link component={RouterLink} to={path} underline="none">
          {renderContent}
        </Link>
      );
    };

    return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
  }
);

NavItem.displayName = 'NavItem';

export default NavItem;
